import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { leadAPI, vendorAPI } from '../../services/api';
import { Lead, LeadFilters } from '../../types';

interface LeadsState {
  leads: Lead[];
  currentLead: Lead | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  } | null;
}

const initialState: LeadsState = {
  leads: [],
  currentLead: null,
  loading: false,
  error: null,
  pagination: null,
};

// Async thunks
export const fetchLeads = createAsyncThunk(
  'leads/fetchLeads',
  async (filters: LeadFilters = {}, { rejectWithValue }) => {
    try {
      const params: Record<string, string | number> = {
        page: filters.page ?? 1,
        limit: filters.limit ?? 10,
        sortBy: filters.sortBy ?? 'createdAt',
        sortOrder: filters.sortOrder ?? 'desc',
      };
      if (filters.status) params.status = filters.status;
      if (filters.store) params.store = filters.store;
      if (filters.listing) params.listing = filters.listing;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.search?.trim()) params.search = filters.search.trim();

      const response = await vendorAPI.getLeads(params);
      const body = response.data as {
        data?: { leads: Lead[] };
        pagination?: LeadsState['pagination'];
      };
      return {
        leads: body.data?.leads ?? [],
        pagination: body.pagination ?? null,
      };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch leads';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchLeadById = createAsyncThunk(
  'leads/fetchLeadById',
  async (leadId: string, { rejectWithValue }) => {
    try {
      const response = await leadAPI.getById(leadId);
      return (response.data as any).data.lead;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch lead';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateLeadStatus = createAsyncThunk(
  'leads/updateLeadStatus',
  async ({ leadId, data }: { leadId: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await vendorAPI.updateLeadStatus(leadId, data);
      return (response.data as any).data.lead;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to update lead status';
      return rejectWithValue(errorMessage);
    }
  }
);

const leadsSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentLead: (state, action) => {
      state.currentLead = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch leads
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload.leads;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch lead by ID
      .addCase(fetchLeadById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeadById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLead = action.payload;
        state.error = null;
      })
      .addCase(fetchLeadById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update lead status
      .addCase(updateLeadStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLeadStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.leads.findIndex(lead => lead._id === action.payload._id);
        if (index !== -1) {
          state.leads[index] = action.payload;
        }
        if (state.currentLead?._id === action.payload._id) {
          state.currentLead = action.payload;
        }
        state.error = null;
      })
      .addCase(updateLeadStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentLead } = leadsSlice.actions;
export default leadsSlice.reducer;
